<script lang="ts">
  import { ActivityTag } from '@/Common/ActivityTag';
  import TagWrapper from '@/Common/TagWrapper.svelte';
  import { cn } from '@/lib/utils';
  import { handleImageError, toDateInputString, toTimeInputString } from '@/Util/utils';
  import { Activity, type Exercise } from '@/Common/ActivityFunctions';
  import SuitabilityBadges from './SuitabilityBadges.svelte';
  import TrainingCardLongActionsSection from './TrainingCardLongActionsSection.svelte';
  import TrainingCardTrainingValues from './TrainingCardTrainingValues.svelte';
  import { Button, buttonVariants } from '@/lib/components/ui/button';
  import { scheduleTraining } from '@/Util/api';
  import { addMinutes } from 'date-fns';
  import * as Popover from '$lib/components/ui/popover/index.js';
  import { Label } from '@/lib/components/ui/label';
  import { Input } from '@/lib/components/ui/input';
  import Separator from '@/lib/components/ui/separator/separator.svelte';
  import SendToWorkoutPlayerButton from './SendToWorkoutPlayerButton.svelte';
  import { getContext } from 'svelte';
  import type { MyFitnessStores } from '../stores';
  import { notifyApiError } from '@/Util/notify';
  import { ErrorCode } from '@/Util/errorCodes';

  export let exercise: Exercise;
  export let size: 'sm' | 'lg' = 'lg';

  export let showChartImage = true;
  export let forceShowDescription = false;
  export let showButtons = true;

  const trainingPlan = getContext<MyFitnessStores>('myFitness')?.trainingPlan;

  let containerSize: ResizeObserverSize[] | null = null;
  let containerWidth: number | null = null;
  $: if (containerSize && containerSize[0]) containerWidth = containerSize[0].inlineSize;

  let schedulePopoverOpen: boolean = false;

  const activityTag = ActivityTag('default');
  let activityTags = [
    activityTag.exerciseType({ ...exercise, exerciseType: exercise.exerciseType ?? '' }),
  ].filter((tag) => tag);

  $: suitabilityList = exercise?.suitability?.split(',').map(s=>s.trim()).filter(Boolean) ?? [];

  // getExerciseType rather than exercise.exerciseType: it reports an Activity that carries a
  // workout_id as a "Workout", which is what someone reading the button would call it.
  $: trainingNoun = Activity.getExerciseType(exercise) ?? 'Training';

  // Scheduling. Default to the top of the next hour: scheduleTraining rejects times in the past,
  // so defaulting to "now" left the value unschedulable by the time the popover was filled in.
  let scheduleDate: string = '';
  let scheduleTime: string = '';
  function resetScheduleDefaults() {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    scheduleDate = toDateInputString(d);
    scheduleTime = toTimeInputString(d);
  }
  // Set from the start, because "Assign to Forecast" reads these and is disabled while they're
  // empty; refreshed on each open so a long-lived page doesn't offer an hour that has since passed.
  resetScheduleDefaults();
  $: if (schedulePopoverOpen) resetScheduleDefaults();

  let scheduleIcon: string = 'fa-calendar-plus';
  $: exercise, scheduleIcon = 'fa-calendar-plus';

  let forecastIcon: string = 'fa-calendar-plus';
  $: exercise, forecastIcon = 'fa-calendar-plus';

  /** `dateTime` defaults to whatever is in the popover's date/time inputs. */
  async function schedule(assignToForecast: boolean = false, dateTime?: Date) {
    if (assignToForecast) {
      forecastIcon = 'fa-spinner-third animate-spin';
    } else {
      scheduleIcon = 'fa-spinner-third animate-spin';
    }
    let result = await scheduleTraining(exercise, dateTime ?? new Date(`${scheduleDate}T${scheduleTime}`), assignToForecast);
    if (assignToForecast) {
      forecastIcon = result.success ? 'fa-check' : 'fa-xmark';
    } else {
      scheduleIcon = result.success ? 'fa-check' : 'fa-xmark';
    }
    if (!result.success) {
      notifyApiError(result, assignToForecast
        ? { code: ErrorCode.SCHEDULE_FORECAST_FAILED, message: 'Could not assign this training to your forecast. Please try again in a moment.' }
        : { code: ErrorCode.SCHEDULE_CALENDAR_FAILED, message: 'Could not add this training to your calendar. Please try again in a moment.' });
    }
  }

  $: hasWorkout = Boolean(exercise?.workout?.path);

</script>

<div bind:borderBoxSize={containerSize} class={cn('flex flex-col gap-1', containerWidth >= 1050 ? 'flex-row' : 'flex-col', 'text-sm')}>
  {#if containerWidth < 1050}
    <h3 class={cn("!whitespace-normal w-full font-medium m-0", size == 'lg' ? 'text-base line-clamp-1' : 'text-sm line-clamp-2')}>
      {#if Activity.getDetailsPageLink(exercise)}
        <a class='text-foreground hover:underline' href={Activity.getDetailsPageLink(exercise)} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
          {exercise.name}
        </a>
      {:else}
        {exercise.name}
      {/if}
    </h3>

    <div class='flex flex-row flex-wrap gap-4'>
      {#if showChartImage}
      <img class='h-max w-[320px] rounded-md border border-border' src={Activity.getOneImage(exercise)?.url ?? Activity.getBackupImageUrl()} alt="Chart" draggable="false" on:error={handleImageError} />
      {/if}
      <div class='flex flex-col gap-4 flex-1 min-w-[250px]'>
        <TrainingCardTrainingValues {exercise} />
        {#if forceShowDescription}
        <section class="space-y-3">
          <h3 class="m-0 text-sm font-medium tracking-wide text-muted-foreground">Description</h3>
          <p class="text-sm max-h-[120px] overflow-hidden [overflow-wrap:anywhere]">{exercise.description}</p>
        </section>
        {/if}
        <TagWrapper tags={activityTags} />
        <SuitabilityBadges suitability={suitabilityList} showFullText isLiked={exercise.liked || exercise.favorite} isDisliked={exercise.disliked} />
      </div>
    </div>

    <Separator orientation="horizontal" />
    
    {#if showButtons}
    <div class='flex flex-row flex-wrap gap-4 p-3'>
      <TrainingCardLongActionsSection {exercise} />
      {#if hasWorkout}
        <SendToWorkoutPlayerButton workout={{ ...exercise, path: exercise.workout?.path ?? exercise.path }} />
      {/if}
      {#if $trainingPlan?.isForecastPlan}
        <Button variant='outline' size="sm" on:click={() => schedule(true)} disabled={!scheduleDate}>
          <i class={`fa-regular ${forecastIcon} text-xs mr-1`}></i>Assign to Forecast
        </Button>
      {/if}
      <Popover.Root bind:open={schedulePopoverOpen} portal="#shadcn-ui-dialog-target">
        <Popover.Trigger class={cn(buttonVariants({ variant: "outline", size: 'sm' }))}>
          <i class={`fa-regular ${scheduleIcon} text-xs mr-1`}></i>Do the {trainingNoun}
        </Popover.Trigger>
        <Popover.Content class="flex flex-col gap-4">
          <div class="flex flex-row justify-between">
            <div class="space-y-1">
              <Label for="selected-date">Date</Label>
              <Input type="date" id="selected-date" bind:value={scheduleDate} />
            </div>
            <div class="space-y-1">
              <Label for="selected-time">Time</Label>
              <Input type="time" id="selected-time" bind:value={scheduleTime} />
            </div>
          </div>
          <div class="flex justify-end gap-2">
            <!-- scheduleTraining rejects times already past, so "now" needs a minute of headroom. -->
            <Button size="sm" variant="outline" on:click={() => {schedule(false, addMinutes(new Date(), 1)); schedulePopoverOpen = false;}}>
              Schedule Now
            </Button>
            <Button size="sm" variant="outline" on:click={() => {schedule(); schedulePopoverOpen = false;}}>
              Schedule
            </Button>
          </div>
        </Popover.Content>
      </Popover.Root>
      <slot/>
    </div>
    {/if}
  {/if}

  {#if containerWidth >= 1050}
    <div class='flex flex-row gap-6 items-center w-full'>
      {#if showChartImage}
      <img class='w-[320px] rounded-md border border-border' src={Activity.getOneImage(exercise)?.url ?? Activity.getBackupImageUrl()} alt="Chart" draggable="false" on:error={handleImageError} />
      {/if}
      <div class='flex min-w-0 flex-1 flex-col gap-2'>
        <h3 class={cn("!whitespace-normal w-full font-medium m-0", size == 'lg' ? 'text-base' : 'text-sm', 'line-clamp-2')}>
          {#if Activity.getDetailsPageLink(exercise)}
            <a class='text-foreground hover:underline' href={Activity.getDetailsPageLink(exercise)} target={Activity.isWorkout(exercise) ? "workout" : "activity"}>
              {exercise.name}
            </a>
          {:else}
            {exercise.name}
          {/if}
        </h3>
        <section class="space-y-3">
          <h3 class="m-0 text-sm font-medium tracking-wide text-muted-foreground">Description</h3>
          <p class="text-sm max-h-[120px] overflow-hidden [overflow-wrap:anywhere]">{exercise.description}</p>
        </section>
        <TagWrapper tags={activityTags} />
        <div class='flex-1' />
        <SuitabilityBadges suitability={suitabilityList} showFullText isLiked={exercise.liked || exercise.favorite} isDisliked={exercise.disliked} />
      </div>
      <div class='flex min-w-[17rem] flex-1 flex-col gap-2'>
        <TrainingCardTrainingValues {exercise} />
        <div class='flex-1' />
        <TrainingCardLongActionsSection {exercise} />
      </div>

      <Separator orientation="vertical" />

      {#if showButtons}
      <div class='flex shrink-0 flex-col gap-4 pr-4'>
        {#if hasWorkout}
          <SendToWorkoutPlayerButton workout={{ ...exercise, path: exercise.workout?.path ?? exercise.path }} />
        {/if}
        {#if $trainingPlan?.isForecastPlan}
          <Button variant='outline' size="sm" on:click={() => schedule(true)} disabled={!scheduleDate}>
            <i class={`fa-regular ${forecastIcon} text-xs mr-1`}></i>Assign to Forecast
          </Button>
        {/if}
        <Popover.Root bind:open={schedulePopoverOpen} portal="#shadcn-ui-dialog-target">
          <Popover.Trigger class={cn(buttonVariants({ variant: "outline", size: 'sm' }))}>
            <i class={`fa-regular ${scheduleIcon} text-xs mr-1`}></i>Do the {trainingNoun}
          </Popover.Trigger>
          <Popover.Content class="flex flex-col gap-4">
            <div class="flex flex-row justify-between">
              <div class="space-y-1">
                <Label for="selected-date">Date</Label>
                <Input type="date" id="selected-date" bind:value={scheduleDate} />
              </div>
              <div class="space-y-1">
                <Label for="selected-time">Time</Label>
                <Input type="time" id="selected-time" bind:value={scheduleTime} />
              </div>
            </div>
            <div class="flex justify-end gap-2">
              <!-- scheduleTraining rejects times already past, so "now" needs a minute of headroom. -->
              <Button size="sm" variant="outline" on:click={() => {schedule(false, addMinutes(new Date(), 1)); schedulePopoverOpen = false;}}>
                Schedule Now
              </Button>
              <Button size="sm" variant="outline" on:click={() => {schedule(); schedulePopoverOpen = false;}}>
                Schedule
              </Button>
            </div>
          </Popover.Content>
        </Popover.Root>
        <slot/>
      </div>
      {/if}
    </div>
  {/if}
</div>
